import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Aurora } from "@/components/motion/aurora";
import { FadeIn } from "@/components/motion/fade-in";
import { Brand } from "@/components/site/brand";
import { ThemeToggle } from "@/components/theme-toggle";

interface AuthLayoutProps {
  title: string;
  subtitle: string;
  children: ReactNode;
  /** Bottom-of-card text, e.g. "Already have an account? Sign in" */
  footer: ReactNode;
}

export function AuthLayout({ title, subtitle, children, footer }: AuthLayoutProps) {
  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Marketing panel — hidden on mobile */}
      <aside className="relative hidden overflow-hidden bg-zinc-950 text-zinc-50 lg:flex">
        {/*
          Marketing panel is always dark (bg-zinc-950). Pin the Aurora's
          edge fade to that same colour so the vignette stays consistent
          in light mode — otherwise it fades to white and the panel
          looks washed out.
        */}
        <Aurora className="opacity-60" fadeColor="rgb(9 9 11)" />
        <div className="relative z-10 flex w-full flex-col justify-between p-12">
          <Brand className="text-zinc-50" />
          <FadeIn immediate delay={0.15} className="space-y-6">
            <p className="text-sm uppercase tracking-widest text-zinc-400">
              The control plane for AI agents
            </p>
            <h2 className="text-balance text-4xl font-bold leading-tight lg:text-5xl">
              Connect every tool once.{" "}
              <span className="bg-gradient-to-r from-indigo-300 via-fuchsia-300 to-teal-300 bg-clip-text text-transparent">
                Expose them everywhere.
              </span>
            </h2>
            <p className="max-w-md text-zinc-300">
              MemKernel is the OS layer underneath your AI agents — tools,
              memory, identity, permissions, all behind one unified interface.
            </p>
            <ul className="space-y-3 text-sm text-zinc-300">
              <li className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                310+ integrations, MCP-ready
              </li>
              <li className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                Personal-account only — no leaky workspaces
              </li>
              <li className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                TypeScript, Python, Go SDKs
              </li>
            </ul>
          </FadeIn>
          <p className="text-xs text-zinc-500">
            © {new Date().getFullYear()} MemKernel. All rights reserved.
          </p>
        </div>
      </aside>

      {/* Auth form */}
      <main className="relative flex min-h-screen flex-col bg-background">
        <div className="flex items-center justify-between px-6 pt-6 lg:hidden">
          <Brand />
          <ThemeToggle />
        </div>
        <div className="absolute right-6 top-6 hidden lg:block">
          <ThemeToggle />
        </div>
        <div className="flex flex-1 items-center justify-center p-6 sm:p-12">
          <div className="w-full max-w-sm space-y-8">
            <FadeIn immediate>
              <div className="text-center lg:text-left">
                <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
                <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>
              </div>
            </FadeIn>
            <FadeIn immediate delay={0.05}>
              {children}
            </FadeIn>
            <FadeIn immediate delay={0.1}>
              <p className="text-center text-sm text-muted-foreground">
                {footer}
              </p>
            </FadeIn>
            <p className="text-center text-xs text-muted-foreground">
              By continuing you agree to our{" "}
              <Link to="/docs/terms" className="underline">
                Terms
              </Link>{" "}
              and{" "}
              <Link to="/docs/privacy" className="underline">
                Privacy Policy
              </Link>
              .
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
